import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Calendar, Play } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { apiUrl } from '@/lib/api'
import { useModal } from '@/contexts/ModalContext'
import { NewsContent, extractHeadings } from './NewsContent'
import type { NewsItem } from '@/types/news'

interface NewsModalProps {
  slug: string | null
  onClose: () => void
}

function getYouTubeId(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.hostname.includes('youtube.com')) {
      return parsed.searchParams.get('v')
    }
    if (parsed.hostname.includes('youtu.be')) {
      return parsed.pathname.slice(1)
    }
  } catch {
    const match = url.match(/(?:v=|\/)([a-zA-Z0-9_-]{11})/)
    return match?.[1] || null
  }
  return null
}

export function NewsModal({ slug, onClose }: NewsModalProps) {
  const { t, i18n } = useTranslation(['blog', 'common'])
  const { setIsModalOpen } = useModal()
  const closeRef = useRef<HTMLButtonElement>(null)
  const [article, setArticle] = useState<NewsItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setIsModalOpen(!!slug)
  }, [slug, setIsModalOpen])

  useEffect(() => {
    if (!slug) {
      setArticle(null)
      return
    }

    setLoading(true)
    setError(null)

    fetch(apiUrl(`/api/news/${encodeURIComponent(slug)}?lang=${i18n.language}`))
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load article')
        return res.json()
      })
      .then((data) => setArticle(data.news))
      .catch(() => setError(t('blog:modal.error', { defaultValue: t('common:error.description') })))
      .finally(() => setLoading(false))
  }, [slug, i18n.language, t])

  useEffect(() => {
    if (slug) {
      document.body.style.overflow = 'hidden'
      closeRef.current?.focus()
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [slug])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const headings = useMemo(() => {
    if (!article?.content) return []
    return extractHeadings(article.content)
  }, [article?.content])

  const videoId = article?.videoUrl ? getYouTubeId(article.videoUrl) : null

  return (
    <AnimatePresence>
      {slug && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={t('blog:modal.title', { defaultValue: 'Article' })}
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-ink-deep/90 backdrop-blur-md" />

          {/* Modal container */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'relative w-full max-w-5xl max-h-[100dvh] md:max-h-[90vh]',
              'bg-ink border-y md:border border-primary/20 md:rounded-2xl',
              'shadow-2xl shadow-black/40 overflow-hidden flex flex-col'
            )}
          >
            {/* Header */}
            <div className="relative shrink-0">
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-ink-deep/80 text-white hover:text-primary hover:bg-ink-deep border border-white/10 transition-colors"
                aria-label={t('common:aria.close')}
              >
                <X className="w-5 h-5" />
              </button>

              {article && (
                <div className="relative aspect-[21/9] md:aspect-[21/8] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="px-2.5 py-1 text-xs font-medium tracking-wide uppercase rounded-full bg-primary text-ink">
                        {article.category}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm text-white/70">
                        <Calendar className="w-4 h-4" />
                        {new Date(article.publishedAt).toLocaleDateString(i18n.language, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl md:text-4xl text-white max-w-3xl">
                      {article.title}
                    </h2>
                  </div>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-20">
                  <span className="text-white/60">{t('common:aria.loading')}…</span>
                </div>
              )}

              {error && (
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                  <p className="text-white/70">{error}</p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-4 text-primary hover:text-primary-light"
                  >
                    {t('common:buttons.backToHome')}
                  </button>
                </div>
              )}

              {article && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 md:p-8">
                  {/* Table of contents */}
                  {headings.length > 0 && (
                    <aside className="lg:col-span-4 order-1 lg:order-1">
                      <div className="lg:sticky lg:top-6 p-5 rounded-xl bg-ink-deep/60 border border-white/10">
                        <h3 className="font-display text-lg text-primary mb-4">
                          {t('blog:modal.toc', { defaultValue: 'In this article' })}
                        </h3>
                        <ul className="space-y-2">
                          {headings.map((heading) => (
                            <li key={heading.id}>
                              <a
                                href={`#${heading.id}`}
                                className="block text-sm text-white/60 hover:text-primary transition-colors"
                                onClick={(e) => {
                                  e.preventDefault()
                                  document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' })
                                }}
                              >
                                {heading.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </aside>
                  )}

                  {/* Article content */}
                  <div className={cn('order-2 lg:order-2', headings.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12')}>
                    {videoId && (
                      <div className="mb-8 rounded-xl overflow-hidden border border-white/10 bg-ink-deep">
                        <div className="aspect-video relative">
                          <iframe
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={t('blog:modal.video', { defaultValue: 'Video' })}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="absolute inset-0 w-full h-full"
                          />
                        </div>
                        <div className="flex items-center gap-2 px-4 py-3 text-xs text-white/50">
                          <Play className="w-3.5 h-3.5" />
                          {t('blog:modal.video', { defaultValue: 'Video' })}
                        </div>
                      </div>
                    )}

                    <NewsContent content={article.content} />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
